import React, { useContext, useEffect, useRef, useState } from 'react';

import { Grid } from '@sunwisesoftware/sunwise-ui';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { createStructuredSelector } from 'reselect';

import {
    handleOpenDrawer,
    loadingUseEffect,
    handleOpenChangeSegments,
} from 'common/components/segment/helpers';
import PageWrapper from 'common/components/segment/PageWrapper';
import StyledGrid from 'common/components/segment/StyledGrid';
import SwipeableDrawer from 'common/components/SwipeableDrawer';
import { LoadingContext } from 'common/utils/contexts';

import * as actions from './actions';
import {
    AddSegmentFormContainer,
    HeaderLayout,
    SegmentEditCard,
    SegmentsBar,
} from './components';
import GridArea from './components/GridArea';
import * as selectors from './selectors';

const Container = ({
    generateProposal,
    handleClickBreadcrumb,
    handleClickEdit,
    initialFetching,
    initialValues,
    isDeletingSegment,
    isDisabledEdition,
    isEditingSegment,
    isFetchingOfferInfoPatern,
    isFetchingParentFieldSegment,
    isGeneratingProposal,
    isSavingSegment,
    match,
    offerInfoPatern,
    offerPanels,
    reset,
    resetForm,
    segments,
    selectedSegmentId,
    selectSegment,
    updateSegments,
}) => {
    const loadingContext = useContext(LoadingContext);
    const sideBarRef = useRef(null);
    const swipeableDrawerRef = useRef(null);
    const [initialValuesGrid, setInitialValuesGrid] = useState(null);

    const onCloseDrawer = () => handleOpenDrawer(swipeableDrawerRef, false);

    useEffect(() => {
        initialFetching(match.params.uid);
        return () => reset();
    }, [match.params.uid, initialFetching, reset]);

    useEffect(() => {
        if (!segments || segments.length === 0) {
            setInitialValuesGrid(null);
            return;
        }

        if (isEmpty(selectedSegmentId)) {
            selectSegment(segments[0], segments);
            handleOpenChangeSegments({
                item: segments[0],
                segments,
                updateSegments,
            });
            return;
        }

        const value = segments.find((s) => s.id === selectedSegmentId);
        if (value) {
            setInitialValuesGrid((prev) =>
                JSON.stringify(prev) === JSON.stringify(value) ? prev : value,
            );
        }
    }, [segments, selectedSegmentId, selectSegment, updateSegments]);

    useEffect(() => {
        if (isEmpty(initialValues)) return;
        setInitialValuesGrid((prev) =>
            JSON.stringify(prev) === JSON.stringify(initialValues)
                ? prev
                : initialValues,
        );
    }, [initialValues]);

    useEffect(
        loadingUseEffect({
            isDeletingSegment,
            isGeneratingProposal,
            isEditingSegment,
            isSavingSegment,
            loadingContext,
        }),
        [
            isDeletingSegment,
            isGeneratingProposal,
            isEditingSegment,
            isSavingSegment,
        ],
    );

    if (isFetchingParentFieldSegment || isFetchingOfferInfoPatern) return null;

    const segmentBar = ({ isMobile = false } = {}) => (
        <SegmentsBar
            commercialOfferId={match.params.uid}
            generateProposal={generateProposal}
            handleClickEdit={handleClickEdit}
            isDisabledEdition={isDisabledEdition}
            isEditionMode={!isEmpty(initialValues)}
            isMobile={isMobile}
            offerPanels={offerPanels}
            onCloseDrawer={onCloseDrawer}
            proposalId={offerInfoPatern.proposalId}
            renderStatus={offerInfoPatern.renderStatus}
            segments={segments}
        />
    );

    return (
        <>
            <Grid container spacing={0}>
                <Grid
                    size={{ lg: 13, md: 11, xs: 18 }}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                    }}
                >
                    <HeaderLayout
                        currentBreadcrumb="Bom Express"
                        handleClickBreadcrumb={handleClickBreadcrumb}
                        offerInfoPatern={offerInfoPatern}
                    />

                    <PageWrapper>
                        <GridArea
                            initialValues={initialValuesGrid}
                            isEditionMode={!isEmpty(initialValues)}
                        />
                    </PageWrapper>
                </Grid>

                <StyledGrid
                    ref={sideBarRef}
                    size={{ lg: 5, md: 7 }}
                    sx={{
                        display: { md: 'block', xs: 'none' },
                        minHeight: {
                            md: 'calc(100vh - 64px)',
                            xs: 'calc(100vh - 105px)',
                        },
                    }}
                >
                    {segmentBar()}
                </StyledGrid>
            </Grid>

            <AddSegmentFormContainer />

            <SwipeableDrawer
                ref={swipeableDrawerRef}
                onCloseEnded={() => resetForm()}
            >
                <>
                    {isEmpty(initialValues) && segmentBar({ isMobile: true })}
                    {!isEmpty(initialValues) && (
                        <SegmentEditCard
                            commercialOfferId={match.params.uid}
                            offerPanels={offerPanels}
                            onCloseDrawer={onCloseDrawer}
                            sxBox={{
                                display: { md: 'none', xs: 'block' },
                                width: '100%',
                            }}
                        />
                    )}
                </>
            </SwipeableDrawer>
        </>
    );
};

const mapStateToProps = createStructuredSelector({
    initialValues: selectors.getInitialValues,
    isDeletingSegment: selectors.getIsDeletingSegment,
    isDisabledEdition: selectors.getIsDisabledEdition,
    isEditingSegment: selectors.getIsEditingSegment,
    isFetchingOfferInfoPatern: selectors.getIsFetchingOfferInfoPatern,
    isFetchingParentFieldSegment: selectors.getIsFetchingParentFieldSegment,
    isGeneratingProposal: selectors.getIsGeneratingProposal,
    isSavingSegment: selectors.getIsSavingSegment,
    offerInfoPatern: selectors.getOfferInfoPaternData,
    offerPanels: selectors.getOfferPanelsData,
    segments: selectors.getSegmentsData,
    selectedSegmentId: selectors.getSelectedSegmentId,
});

const mapDispatchToProps = (dispatch) => ({
    generateProposal: (proposalId) =>
        dispatch(actions.generateProposal(proposalId)),
    handleClickBreadcrumb: (contactId, projetcId, proposalId) =>
        dispatch(
            actions.handleClickBreadcrumb(contactId, projetcId, proposalId),
        ),
    handleClickEdit: (layoutId, proposalId) =>
        dispatch(actions.prepareEdit(layoutId, proposalId)),
    initialFetching: (id) => dispatch(actions.initialFetching(id)),
    prepareEditSegment: (values) => dispatch(actions.setInitialValues(values)),
    reset: () => dispatch(actions.resetValues()),
    resetForm: () => dispatch(actions.resetForm()),
    selectSegment: (segment) => dispatch(actions.selectSegment(segment)),
    updateSegments: (values) => dispatch(actions.updateSegments(values)),
});

Container.propTypes = {
    generateProposal: PropTypes.func,
    handleClickBreadcrumb: PropTypes.func,
    handleClickEdit: PropTypes.func,
    initialFetching: PropTypes.func,
    initialValues: PropTypes.object,
    isDeletingSegment: PropTypes.bool,
    isDisabledEdition: PropTypes.bool,
    isEditingSegment: PropTypes.bool,
    isFetchingOfferInfoPatern: PropTypes.bool,
    isFetchingParentFieldSegment: PropTypes.bool,
    isGeneratingProposal: PropTypes.bool,
    isSavingSegment: PropTypes.bool,
    match: PropTypes.object,
    offerInfoPatern: PropTypes.object,
    offerPanels: PropTypes.array,
    reset: PropTypes.func,
    resetForm: PropTypes.func,
    segments: PropTypes.array,
    selectedSegmentId: PropTypes.string,
    selectSegment: PropTypes.func,
    updateSegments: PropTypes.func,
};

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    withRouter,
)(Container);
